import type { JSX } from 'react';
import { createElement, HTMLAttributes } from 'react';

import cn from 'classnames';

import { textSprinkles, type TextSprinkles } from '../../styles/sprinkles.css';
import { t, type TypographyVariants } from '../../typography';

type TextVariant = NonNullable<TypographyVariants['variant']>;
type TextElement = keyof JSX.IntrinsicElements;

const defaultVariantMapping: Record<NonNullable<TextVariant>, TextElement> = {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    p: 'p',
    pLarge: 'p',
    pSmall: 'p',
    label: 'label',
    small: 'small',
    strong: 'strong',
};

type TextOwnProps = {
    variant?: TextVariant;
    color?: TypographyVariants['color'];
    as?: TextElement;
} & Partial<Omit<TextSprinkles, 'color'>>;

export type TextProps = TextOwnProps & Omit<HTMLAttributes<HTMLElement>, 'color'>;

export const Text = ({
    variant = 'p',
    className,
    as,
    textAlign,
    whiteSpace,
    marginBlock,
    marginBlockStart,
    marginBlockEnd,
    marginInline,
    marginInlineStart,
    marginInlineEnd,
    paddingBlock,
    paddingBlockStart,
    paddingBlockEnd,
    paddingInline,
    paddingInlineStart,
    paddingInlineEnd,
    fontWeight,
    textTransform,
    weight,
    transform,
    m,
    my,
    mx,
    mt,
    mb,
    ml,
    mr,
    p,
    py,
    px,
    pt,
    pb,
    pl,
    pr,
    color = 'inherit',
    fontSize,
    ...props
}: TextProps) => {
    const Component = as ?? defaultVariantMapping[variant];

    const sprinkleClasses = textSprinkles({
        marginBlock,
        marginBlockStart,
        marginBlockEnd,
        marginInline,
        marginInlineStart,
        marginInlineEnd,
        paddingBlock,
        paddingBlockStart,
        paddingBlockEnd,
        paddingInline,
        paddingInlineStart,
        paddingInlineEnd,
        fontWeight,
        textTransform,
        weight,
        transform,
        m,
        my,
        mx,
        mt,
        mb,
        ml,
        mr,
        p,
        py,
        px,
        pt,
        pb,
        pl,
        pr,
        textAlign,
        whiteSpace,
        fontSize,
    });
    const classes = cn(sprinkleClasses, t({ variant, color }), className);
    return createElement(Component, { className: classes, ...props });
};
